<template>
  <div class="search-persons">
    <WLabel v-if="label">{{ label }}</WLabel>
    <div class="search-persons__container">
      <HTextfield
        ref="input"
        v-model:value="value"
        icon="icSearch"
        :mask-pattern="mask"
        class="search-persons__input"
        :auto-focus="autofocus"
        :placeholder="placeholder"
        :required="required"
      />
      <transition v-if="showClearButton" name="button-fade">
        <div class="search-persons__clear">
          <WButton
            v-show="value || isSelected"
            variant="tertiary"
            icon-button
            size="small"
            icon="icClose"
            @click.prevent="clear"
          />
        </div>
      </transition>
    </div>

    <div v-if="value && !isSelected" class="search-persons__results">
      <span v-if="loading" class="search-persons__results-loading">
        Carregando...
      </span>
      <span v-else-if="!results.length" class="search-persons__results-empty">
        Não encontramos nenhuma pessoa
      </span>
      <ul v-else class="search-persons__results-list">
        <li
          v-for="(result, i) in results"
          :key="`${result.nationalId}-${i}`"
          class="search-persons__results-item"
        >
          <div
            :class="{
              'search-persons__results-link': true,
              'search-persons__results-link--large': large
            }"
            @click="selectUser(result)"
          >
            <span class="search-persons__results-full-name">
              {{ result.firstName }} {{ result.lastName }}
            </span>
            <span class="search-persons__results-date-of-birth">
              {{ result.dateOfBirth }}
            </span>
            <span class="search-persons__results-national-id">
              {{ formatNationalId(result.nationalId) }}
            </span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { WButton, WLabel } from '@alice-health/wonderland-vue'
import { format as formatCPF } from '@fnando/cpf'
import debounce from 'lodash/debounce'

import { HTextfield } from '../atoms'

export default {
  name: 'SearchPersons',
  components: { WButton, WLabel, HTextfield },
  props: {
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Buscar pessoa por código interno, cpf ou nome.'
    },
    required: {
      type: Boolean,
      default: false
    },
    personId: {
      type: [String, undefined],
      default: undefined
    },
    showClearButton: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: true
    },
    large: Boolean
  },
  emits: ['select'],
  data() {
    return {
      value: '',
      loading: false,
      results: [],
      abortController: undefined,
      isSelected: false,
      personName: ''
    }
  },
  computed: {
    mask() {
      if (this.rawValue && isFinite(this.rawValue)) {
        return '###.###.###-##'
      }

      return undefined
    },
    rawValue() {
      return this.value.replaceAll('.', '').replaceAll('-', '')
    }
  },
  watch: {
    value() {
      if (this.value === this.personName) {
        this.personName = ''
        return
      }

      this.loading = true
      this.searchPersons()
    },
    personId() {
      this.getPerson()
    }
  },
  mounted() {
    this.getPerson()
  },
  methods: {
    searchPersons: debounce(async function () {
      this.abortController?.abort()
      if (this.value === '') {
        this.clear()
        this.loading = false
        return this.loading
      }

      try {
        this.isSelected = false
        this.abortController = new AbortController()
        const { signal } = this.abortController

        const { personSearchResponses } = await this.$store.dispatch(
          'member/searchByQuery',
          {
            query: this.rawValue,
            signal
          }
        )

        this.results = personSearchResponses
        this.loading = false
      } catch (error) {
        this.results = []
        this.loading = error.name === 'AbortError'
      }
    }, 500),
    selectUser(result) {
      this.isSelected = true
      this.$emit('select', result)
      this.value = `${result.firstName} ${result.lastName}`
      this.personName = this.value
      this.$refs.input?.focus()
    },
    clear() {
      this.value = ''
      this.results = []
      this.$refs.input.focus()
      this.isSelected = false
      this.$emit('select', undefined)
    },
    async getPerson() {
      if (!this.personId) return

      const person = await this.$store.dispatch(
        'member/getPersonById',
        this.personId
      )

      this.selectUser(person)
    },
    formatNationalId(value) {
      return formatCPF(value)
    }
  }
}
</script>

<style lang="scss">
.search-persons {
  position: relative;

  &__container {
    position: relative;
  }

  &__input {
    display: block;
    width: 100%;
  }

  &__results {
    box-shadow: var(--gl-shadow-level-03);
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid var(--gl-color-shades-gray-20);
    position: absolute;
    width: 100%;
    z-index: 10;
    margin-top: 12px;
    max-height: 400px;
    overflow-y: auto;
  }

  &__results-loading,
  &__results-empty {
    color: var(--gl-color-shades-gray-30);
    display: block;
    padding: 16px 32px;
  }

  &__results-list {
    list-style: none;
    padding: 0;
  }

  &__results-item {
    &:not(:last-child) {
      border-bottom: var(--gl-border-width-xs) solid
        var(--gl-color-shades-gray-20);
    }
  }

  &__results-link {
    align-items: center;
    color: var(--sys-color-content-tertiary);
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: space-between;
    padding: 16px 24px;
    text-decoration: none;
    width: 100%;

    &:hover,
    &:focus {
      color: var(--sys-color-content-active);
      text-decoration: none;
    }

    &--large {
      grid-template-columns: 2fr auto 100px;
      gap: var(--gl-spacing-06);
    }
  }

  &__results-full-name {
    grid-column: 1/3;
    font-weight: var(--gl-font-weight-semibold);
  }

  &__results-link--large &__results-full-name {
    grid-column: 1;
  }

  &__results-date-of-birth,
  &__results-national-id {
    font-size: var(--gl-font-size-xxs);
    line-height: var(--gl-line-height-xxs);
    font-weight: var(--gl-font-weight-light);
  }

  &__results-date-of-birth {
    text-align: left;
  }

  &__results-national-id {
    text-align: right;
    white-space: nowrap;
  }

  &__clear {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    button {
      margin: auto;
      height: 100%;
    }

    display: flex;
    align-items: center;
  }

  .button-fade-enter-active,
  .button-fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .button-fade-enter-from,
  .button-fade-leave-to {
    opacity: 0;
  }
}
</style>
